import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  InputAdornment,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTimeOutlined";
import { useEffect, useMemo, useState } from "react";
import {
  DAYS_OF_WEEK,
  type DayOfWeek,
  type PmsConfig,
} from "@shared/types/foyer";
import { foyerApi } from "../http/foyer";
import { PageHeader } from "../components/PageHeader";
import LinkConnectionsPanel from "../components/link/LinkConnectionsPanel";

const DAY_LABEL: Record<DayOfWeek, string> = {
  mon: "Monday",
  tue: "Tuesday",
  wed: "Wednesday",
  thu: "Thursday",
  fri: "Friday",
  sat: "Saturday",
  sun: "Sunday",
};

interface Props {
  premiseId: string;
}

function emptyConfig(): PmsConfig {
  return {
    vendor: null,
    dailyReset: false,
    scheduledCheckInOut: {
      enabled: false,
      schedule: {},
    },
  };
}

export default function PmsConfigurationPage({ premiseId }: Props) {
  const [draft, setDraft] = useState<PmsConfig | null>(null);
  const [original, setOriginal] = useState<PmsConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{
    msg: string;
    severity: "success" | "error";
  } | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    foyerApi
      .getPmsConfig(premiseId)
      .then((cfg) => {
        if (cancelled) return;
        const next = { ...emptyConfig(), ...cfg };
        setDraft(next);
        setOriginal(next);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [premiseId]);

  const dirty = useMemo(
    () =>
      !!draft &&
      !!original &&
      JSON.stringify(draft) !== JSON.stringify(original),
    [draft, original]
  );

  async function save() {
    if (!draft) return;
    setSaving(true);
    try {
      const persisted = await foyerApi.putPmsConfig(premiseId, draft);
      const next = { ...emptyConfig(), ...persisted };
      setDraft(next);
      setOriginal(next);
      setToast({ msg: "Saved", severity: "success" });
    } catch (e) {
      setToast({
        msg: e instanceof Error ? e.message : String(e),
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  }

  function setDay(
    day: DayOfWeek,
    field: "checkIn" | "checkOut",
    value: string | undefined
  ) {
    if (!draft) return;
    const schedule = { ...(draft.scheduledCheckInOut?.schedule ?? {}) };
    const existing = schedule[day] ?? {};
    const next = { ...existing, [field]: value };
    // Strip undefined keys so empty entries serialise cleanly.
    const cleaned: { checkIn?: string; checkOut?: string } = {};
    if (next.checkIn !== undefined) cleaned.checkIn = next.checkIn;
    if (next.checkOut !== undefined) cleaned.checkOut = next.checkOut;
    if (cleaned.checkIn === undefined && cleaned.checkOut === undefined) {
      delete schedule[day];
    } else {
      schedule[day] = cleaned;
    }
    setDraft({
      ...draft,
      scheduledCheckInOut: {
        enabled: draft.scheduledCheckInOut?.enabled ?? false,
        schedule,
      },
    });
  }

  function toggleDay(
    day: DayOfWeek,
    field: "checkIn" | "checkOut",
    enable: boolean
  ) {
    // Enabling = ensure a value exists (default 15:00 checkin / 11:00
    // checkout - common hotel norms). Disabling = strip the field.
    if (!enable) {
      setDay(day, field, undefined);
      return;
    }
    const current = draft?.scheduledCheckInOut?.schedule?.[day]?.[field];
    if (current) return;
    setDay(day, field, field === "checkIn" ? "15:00" : "11:00");
  }

  async function runBulk(kind: "in" | "out") {
    setBulkBusy(true);
    try {
      const res =
        kind === "in"
          ? await foyerApi.checkInAll(premiseId)
          : await foyerApi.checkOutAll(premiseId);
      setToast({
        msg:
          kind === "in"
            ? `Checked in ${res.changed} rooms`
            : `Checked out ${res.changed} rooms`,
        severity: "success",
      });
    } catch (e) {
      setToast({
        msg: e instanceof Error ? e.message : String(e),
        severity: "error",
      });
    } finally {
      setBulkBusy(false);
    }
  }

  if (loading) {
    return (
      <Box sx={{ p: 6, textAlign: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !draft) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">{error ?? "Failed to load PMS config"}</Alert>
      </Box>
    );
  }

  const schedEnabled = draft.scheduledCheckInOut?.enabled ?? false;

  return (
    <Stack spacing={3} data-testid="pms-config-page">
      <PageHeader title="PMS Configuration" />

      {/* ⚠️ The hardcoded five-vendor picker that used to live here is GONE
          (INT-11). It offered Protel, Apaleo and Cloudbeds while the folio seam
          knew only manual/mews/opera, so selecting one stored a free string no
          seam could act on and produced no refusal at all — the exact failure
          `folioPostingProviders.ts` was written to close. The panel below has
          no vendor list of its own: it renders the adapter registry. */}
      <LinkConnectionsPanel premiseId={premiseId} />

      <Card variant="outlined">
        <CardContent>
          <Stack spacing={1.5}>
            <FormControlLabel
              control={
                <Switch
                  checked={!!draft.dailyReset}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      dailyReset: e.target.checked,
                      // Seed a sensible default the first time it's enabled.
                      dailyResetTime:
                        e.target.checked && !draft.dailyResetTime
                          ? "00:00"
                          : draft.dailyResetTime,
                    })
                  }
                  data-testid="pms-daily-reset"
                />
              }
              label="Daily reset"
            />
            {draft.dailyReset && (
              <Time24Input
                value={draft.dailyResetTime ?? "00:00"}
                onChange={(v) => setDraft({ ...draft, dailyResetTime: v })}
                label="Reset at"
                testId="pms-daily-reset-time"
              />
            )}
            <Typography variant="caption" color="text.secondary">
              At the configured local time, mark every room as unoccupied.
            </Typography>
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Stack spacing={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={schedEnabled}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      scheduledCheckInOut: {
                        enabled: e.target.checked,
                        schedule: draft.scheduledCheckInOut?.schedule ?? {},
                      },
                    })
                  }
                  data-testid="pms-sched-enabled"
                />
              }
              label="Enable automatic check-in / check-out"
            />
            <Typography variant="caption" color="text.secondary">
              Times are 24h, local to the premise timezone. Tick the actions to
              enable for each day and set when they fire.
            </Typography>
            <Box sx={{ opacity: schedEnabled ? 1 : 0.5 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 140, color: "text.secondary" }}>
                      Day
                    </TableCell>
                    <TableCell align="center" sx={{ color: "text.secondary" }}>
                      Enable Check-In
                    </TableCell>
                    <TableCell align="center" sx={{ color: "text.secondary" }}>
                      Enable Check-Out
                    </TableCell>
                    <TableCell sx={{ width: 170, color: "text.secondary" }}>
                      Check-In Time
                    </TableCell>
                    <TableCell sx={{ width: 170, color: "text.secondary" }}>
                      Check-Out Time
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {DAYS_OF_WEEK.map((day) => {
                    const entry =
                      draft.scheduledCheckInOut?.schedule?.[day] ?? {};
                    const cinOn = entry.checkIn !== undefined;
                    const coutOn = entry.checkOut !== undefined;
                    return (
                      <TableRow key={day}>
                        <TableCell>{DAY_LABEL[day]}</TableCell>
                        <TableCell align="center">
                          <Checkbox
                            size="small"
                            checked={cinOn}
                            disabled={!schedEnabled}
                            onChange={(e) =>
                              toggleDay(day, "checkIn", e.target.checked)
                            }
                            inputProps={
                              {
                                "aria-label": `Enable check-in ${DAY_LABEL[day]}`,
                                "data-testid": `pms-${day}-checkin-enabled`,
                              } as React.InputHTMLAttributes<HTMLInputElement>
                            }
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Checkbox
                            size="small"
                            checked={coutOn}
                            disabled={!schedEnabled}
                            onChange={(e) =>
                              toggleDay(day, "checkOut", e.target.checked)
                            }
                            inputProps={
                              {
                                "aria-label": `Enable check-out ${DAY_LABEL[day]}`,
                                "data-testid": `pms-${day}-checkout-enabled`,
                              } as React.InputHTMLAttributes<HTMLInputElement>
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <Time24Input
                            value={entry.checkIn ?? ""}
                            placeholder="12:00"
                            onChange={(v) => setDay(day, "checkIn", v)}
                            disabled={!schedEnabled || !cinOn}
                            testId={`pms-${day}-checkin`}
                          />
                        </TableCell>
                        <TableCell>
                          <Time24Input
                            value={entry.checkOut ?? ""}
                            placeholder="13:00"
                            onChange={(v) => setDay(day, "checkOut", v)}
                            disabled={!schedEnabled || !coutOn}
                            testId={`pms-${day}-checkout`}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Stack spacing={1.5}>
            <Typography variant="subtitle1">Manual bulk actions</Typography>
            <Typography variant="caption" color="text.secondary">
              Apply check-in or check-out across every room in this premise
              right now. Doesn&apos;t change saved schedule settings.
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                disabled={bulkBusy}
                onClick={() => runBulk("in")}
                data-testid="pms-bulk-checkin"
              >
                Check in all rooms
              </Button>
              <Button
                variant="outlined"
                disabled={bulkBusy}
                onClick={() => runBulk("out")}
                data-testid="pms-bulk-checkout"
              >
                Check out all rooms
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          disabled={!dirty || saving}
          onClick={save}
          data-testid="pms-save"
        >
          {saving ? "Saving…" : "Save"}
        </Button>
      </Box>

      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        {toast ? (
          <Alert severity={toast.severity}>{toast.msg}</Alert>
        ) : undefined}
      </Snackbar>
    </Stack>
  );
}

/**
 * 24-hour HH:mm input - a plain text field with pattern enforcement so
 * the displayed format is consistent across browsers (the native
 * `type="time"` control flips to 12h on Safari + macOS-region users).
 */
function Time24Input({
  value,
  onChange,
  disabled,
  label,
  placeholder = "HH:mm",
  testId,
}: {
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  label?: string;
  placeholder?: string;
  testId: string;
}) {
  return (
    <TextField
      size="small"
      label={label}
      placeholder={placeholder}
      value={value}
      disabled={disabled}
      onChange={(e) => {
        // Allow live typing - accept anything; we'll normalise on blur.
        onChange(e.target.value);
      }}
      onBlur={(e) => {
        const v = normaliseHHmm(e.target.value);
        if (v !== value) onChange(v);
      }}
      InputLabelProps={{ shrink: true }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start" sx={{ mr: 0.5 }}>
            <AccessTimeIcon fontSize="small" color="action" />
          </InputAdornment>
        ),
      }}
      inputProps={{
        inputMode: "numeric",
        maxLength: 5,
        pattern: "([01][0-9]|2[0-3]):[0-5][0-9]",
        "data-testid": testId,
      }}
      sx={{ width: 150 }}
    />
  );
}

/**
 * Coerce loose input to "HH:mm" 24h format. Accepts "1500", "15:00",
 * "15.00", "9:5" → "09:05". Anything unparseable becomes "".
 */
function normaliseHHmm(input: string): string {
  const digits = input.replace(/[^0-9]/g, "");
  if (digits.length === 0) return "";
  let h = 0;
  let m = 0;
  if (digits.length <= 2) {
    h = Number(digits);
  } else if (digits.length === 3) {
    h = Number(digits.slice(0, 1));
    m = Number(digits.slice(1));
  } else {
    h = Number(digits.slice(0, 2));
    m = Number(digits.slice(2, 4));
  }
  if (!Number.isFinite(h) || !Number.isFinite(m)) return "";
  if (h < 0 || h > 23 || m < 0 || m > 59) return "";
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}
